import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, styled } from '@mui/material';
import { useForm } from 'react-hook-form';

import { ACCESS_MESSAGES } from '../../constants/messages.js';
import { RegistrationStep } from '../../constants/steps.js';
import { useRegistration } from '../../contexts/RegistrationContext.js';
import { accessSchema, type AccessFormData } from '../../validation/schemas.js';
import FormField from '../shared/FormField.js';
import { BaseText, PrimaryButton } from '../shared/index.js';

const InfoContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  gap: 12,
  marginTop: 24,
  padding: 16,
}));

const baseTextStyle = {
  fontFamily: 'Open Sans',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '16px',
  verticalAlign: 'middle',
} as const;

const infoTextStyle = {
  fontFamily: 'Open Sans',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
} as const;

const placeholderTextStyle = {
  '& .MuiInputBase-input::placeholder': {
    fontSize: 13,
  },
} as const;

const Access = () => {
  const { accessInfo, completeStep, setAccessInfo, setCurrentStep, setError } = useRegistration();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AccessFormData>({
    resolver: yupResolver(accessSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: accessInfo,
  });

  function onSubmit(data: AccessFormData) {
    setError(undefined);
    setAccessInfo({
      requestedAccessApplication: data.requestedAccessApplication ?? '',
      requestedRole: data.requestedRole ?? '',
    });
    completeStep(RegistrationStep.ACCESS);
    setCurrentStep(RegistrationStep.PASSWORD);
  }

  return (
    <Box data-testid="custom-register-access-step">
      <form data-testid="custom-register-access-form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Box>
            <FormField
              control={control}
              data-testid="custom-register-requested-access-application-input"
              errors={errors}
              label={ACCESS_MESSAGES.APPLICATIONS_LABEL}
              name="requestedAccessApplication"
              placeholder={ACCESS_MESSAGES.APPLICATIONS_PLACEHOLDER}
              sx={placeholderTextStyle}
              required={false}
            />
          </Box>
          <Box>
            <FormField
              control={control}
              data-testid="custom-register-requested-role-input"
              errors={errors}
              label={ACCESS_MESSAGES.ROLE_LABEL}
              name="requestedRole"
              placeholder={ACCESS_MESSAGES.ROLE_PLACEHOLDER}
              sx={placeholderTextStyle}
              required={false}
            />
          </Box>
        </Stack>

        <InfoContainer data-testid="custom-register-access-info-block">
          <InfoOutlinedIcon color="action" data-testid="custom-register-access-info-icon" />
          <BaseText data-testid="custom-register-access-info-text" sx={infoTextStyle}>
            If you need further access after account creation, please submit a Support request for
            assistance.
          </BaseText>
        </InfoContainer>

        <PrimaryButton data-testid="custom-register-access-next-button" type="submit">
          Next
        </PrimaryButton>
      </form>
    </Box>
  );
};

export default Access;
